const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/$/, '');
const accessTokens = new Map();
const REQUEST_TIMEOUT_MS = 12000;

function timeoutError() {
  const error = new Error('Request timed out');
  error.code = 'REQUEST_TIMEOUT';
  error.status = 0;
  return error;
}

function invalidResponseError(status) {
  const error = new Error('Driver Live returned an invalid response');
  error.code = 'INVALID_RESPONSE';
  error.status = status || 0;
  return error;
}

function timedSignal(externalSignal, timeoutMs = REQUEST_TIMEOUT_MS) {
  const controller = new AbortController();
  let timedOut = false;
  let rejectAbort;
  const abortPromise = new Promise((_, reject) => { rejectAbort = reject; });
  const onInternalAbort = () => {
    if (timedOut) rejectAbort(timeoutError());
    else if (controller.signal.reason instanceof Error) rejectAbort(controller.signal.reason);
    else {
      const error = new Error('Request aborted');
      error.name = 'AbortError';
      rejectAbort(error);
    }
  };
  controller.signal.addEventListener('abort', onInternalAbort, { once: true });

  const onAbort = () => controller.abort(externalSignal?.reason);
  if (externalSignal?.aborted) onAbort();
  else externalSignal?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    abortPromise,
    cleanup() {
      clearTimeout(timer);
      externalSignal?.removeEventListener('abort', onAbort);
      controller.signal.removeEventListener('abort', onInternalAbort);
    },
    didTimeOut: () => timedOut,
  };
}

async function fetchJsonWithTimeout(url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
  const timed = timedSignal(options.signal, timeoutMs);
  try {
    const response = await Promise.race([
      fetch(url, { ...options, signal: timed.signal }),
      timed.abortPromise,
    ]);
    let data;
    try {
      // Keep the timeout and caller cancellation active through full body
      // consumption. fetch() resolving headers is not completion of a JSON request.
      data = await Promise.race([response.json(), timed.abortPromise]);
    } catch (error) {
      if (timed.didTimeOut()) throw timeoutError();
      if (timed.signal.aborted) throw error;
      if (response.ok) throw invalidResponseError(response.status);
      data = null;
    }
    if (!response.ok) {
      const error = new Error(data?.error || `Request failed (${response.status})`);
      error.status = response.status;
      error.code = data?.code || null;
      throw error;
    }
    if (data == null || typeof data !== 'object') throw invalidResponseError(response.status);
    return { response, data };
  } catch (error) {
    if (timed.didTimeOut() && error?.code !== 'REQUEST_TIMEOUT') throw timeoutError();
    throw error;
  } finally {
    timed.cleanup();
  }
}

// Test-only named export so the CRA/Jest regression suite can exercise delayed
// response bodies without routing those cases through application state.
export const __testFetchJsonWithTimeout = fetchJsonWithTimeout;

export function getDriverLiveAccessToken(raceId) {
  return accessTokens.get(String(raceId)) || null;
}

export function clearDriverLiveAccessToken(raceId) {
  accessTokens.delete(String(raceId));
}

export async function requestDriverLiveAccess(raceId, code, signal) {
  const { data } = await fetchJsonWithTimeout(`${BASE_URL}/races/${raceId}/driver-live/access`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
    signal,
  });
  if (!data.accessToken || typeof data.accessToken !== 'string') throw invalidResponseError(200);
  accessTokens.set(String(raceId), data.accessToken);
  return data;
}

async function authorizedFetch(raceId, path, { signal } = {}) {
  const token = getDriverLiveAccessToken(raceId);
  if (!token) {
    const error = new Error('Driver Live access required');
    error.status = 401;
    error.code = 'ACCESS_REQUIRED';
    throw error;
  }
  try {
    const { data } = await fetchJsonWithTimeout(`${BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    return data;
  } catch (error) {
    if ([401, 403, 410].includes(error?.status)) clearDriverLiveAccessToken(raceId);
    throw error;
  }
}

export function getDriverLiveDrivers(raceId, signal, mode = 'live') {
  return authorizedFetch(raceId, `/races/${raceId}/driver-live/drivers${mode === 'pub' ? '?mode=pub' : ''}`, { signal });
}

export function getDriverLiveSnapshot(raceId, driverNumber, signal, mode = 'live') {
  return authorizedFetch(
    raceId,
    `/races/${raceId}/driver-live/snapshot?driver_number=${encodeURIComponent(driverNumber)}${mode === 'pub' ? '&mode=pub' : ''}`,
    { signal }
  );
}

function parseSseBlock(block) {
  let event = 'message';
  const data = [];
  for (const line of block.split(/\r?\n/)) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
  }
  return { event, data: data.join('\n') };
}

export async function streamDriverLive(raceId, driverNumber, {
  signal, onData, onAccessExpired, onActivity, onStatus, mode = 'live',
} = {}) {
  const token = getDriverLiveAccessToken(raceId);
  if (!token) {
    const error = new Error('Driver Live access required');
    error.status = 401;
    error.code = 'ACCESS_REQUIRED';
    throw error;
  }

  const response = await fetch(
    `${BASE_URL}/races/${raceId}/driver-live/stream?driver_number=${encodeURIComponent(driverNumber)}${mode === 'pub' ? '&mode=pub' : ''}`,
    { headers: { Authorization: `Bearer ${token}` }, signal }
  );
  onActivity?.();
  if (!response.ok) {
    if ([401, 403, 410].includes(response.status)) clearDriverLiveAccessToken(raceId);
    let data = null;
    try { data = await response.json(); } catch { /* streaming error response may be non-JSON */ }
    const error = new Error(data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.code = data?.code || null;
    throw error;
  }
  if (!response.body) throw new Error('Streaming is not supported by this browser');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    onActivity?.();
    buffer += decoder.decode(value, { stream: true });
    let boundary;
    while ((boundary = buffer.search(/\r?\n\r?\n/)) >= 0) {
      const separator = buffer.match(/\r?\n\r?\n/);
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + separator[0].length);
      if (!block) continue;
      // Comments are server keepalives. They deliberately count as transport
      // activity but do not pretend a driver measurement has changed.
      if (block.startsWith(':')) { onActivity?.(); continue; }
      const parsed = parseSseBlock(block);
      if (parsed.event === 'access-expired') {
        clearDriverLiveAccessToken(raceId);
        let payload = null;
        try { payload = JSON.parse(parsed.data); } catch { /* ignore */ }
        onAccessExpired?.(payload?.code || 'DRIVER_LIVE_ACCESS_EXPIRED');
        return;
      }
      if (parsed.event === 'status' && parsed.data) {
        try { onStatus?.(JSON.parse(parsed.data)); } catch { onStatus?.({ status: 'transport_failure' }); }
      }
      if (parsed.event === 'message' && parsed.data) {
        try { onData?.(JSON.parse(parsed.data)); } catch { /* Ignore malformed frame. */ }
      }
    }
  }
}
